import { useState, useMemo, useCallback, useEffect } from "react";
import { Modal } from "@/components/ui/modal";
import { useI18n } from "@/hooks/useI18n";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useRateHawkStays } from "../ratehawk/useRateHawkStays";
import type {
  RateHawkSearchParams,
  RateHawkSearchFilter,
  RateHawkSearchResult,
  RateHawkRate,
  RateHawkRoomGroup,
  RateHawkRoomGuestConfig,
  RateHawkSearchMode,
  RateHawkSearchSort,
  RateHawkPageSize,
} from "../ratehawk/types";
import type { AccommodationActivity } from "../models/activity.model";
import { DayAxis, addDays, diffDays, isTemplateAxis, stayAnchor } from "../models/day-axis";
import type { IGuest } from "@/lib/request-trip/models/travel-request.model";
import {
  buildRateHawkGuests,
  countGuestsFromRateHawkData,
  convertRateHawkToPlace2BeAccommodation,
} from "../ratehawk/ratehawk-accommodation/helpers";
import { SearchWithResults } from "../ratehawk/ratehawk-accommodation/SearchWithResults";
import type { CityAutocompletePlace } from "../shared/CityAutocomplete";
import { RatesView } from "../ratehawk/ratehawk-accommodation/RatesView";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

type Step = "search" | "rates";

interface RateHawkAccommodationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<AccommodationActivity>) => void;
  destinations?: string[];
  startDate?: string | null;
  endDate?: string | null;
  currentDayDate?: string;
  /** Asse dei giorni: sui template le date servono solo a interrogare RateHawk. */
  axis?: DayAxis | null;
  guestsData?: IGuest[];
  totalGuests?: number;
}

export function RateHawkAccommodationModal({
  isOpen,
  onClose,
  onSave,
  destinations,
  startDate,
  endDate,
  currentDayDate,
  axis,
  guestsData,
  totalGuests,
}: RateHawkAccommodationModalProps) {
  const { t } = useI18n();
  const {
    searchResults,
    totalHotels,
    totalFiltered,
    page,
    totalPages,
    facets,
    // `searchMode` del hook (= ramo effettivamente usato dal server) non serve
    // qui: la modalità la sceglie l'utente ed è stato locale. Resta esposto
    // dal hook per diagnostica.
    isSearching,
    searchError,
    searchStays,
    roomGroups,
    selectedHotel,
    isLoadingRates,
    ratesError,
    fetchRates,
    clearSearch,
    clearRates,
    isMockMode,
  } = useRateHawkStays();

  const [step, setStep] = useState<Step>("search");
  const [residency, setResidency] = useState("it");

  // Su un template non ci sono date reali: quelle inserite qui servono solo a
  // ottenere tariffe indicative. Il soggiorno viene poi ancorato ai giorni relativi.
  const isTemplate = isTemplateAxis(axis);
  // Default sensato per una ricerca "di riferimento": abbastanza avanti nel tempo
  // da avere disponibilità, ma non talmente lontano da non avere tariffe.
  const referenceCheckIn = useMemo(() => addDays(new Date().toISOString().split("T")[0], 30), []);

  // ── Search form state ──────────────────────────────────────────────────────
  const [cityName, setCityName] = useState("");
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  // ETG region id della destinazione scelta. Significativo solo in modalità
  // "region"; viene trasportato fino a stays-search-rh, che lo valida.
  const [regionId, setRegionId] = useState<number | undefined>(undefined);
  // Modalità scelta dall'utente: destinazione (default) oppure coordinate
  // scelte a mano sulla mappa. Le due sono pari grado, non una il fallback
  // dell'altra.
  const [searchMode, setSearchMode] = useState<RateHawkSearchMode>("region");
  const [checkIn, setCheckIn] = useState(isTemplate ? referenceCheckIn : (currentDayDate?.split("T")[0] || ""));
  const [checkOut, setCheckOut] = useState("");
  const [rooms, setRooms] = useState<RateHawkRoomGuestConfig[]>([{ adults: 2, childrenAges: [] }]);
  const [radius, setRadius] = useState(10);

  // ── Stato della "vista" sui risultati ─────────────────────────────────────
  //
  // Filtri, ordinamento e paginazione sono tutti server-side: la edge function
  // li applica sull'intera ricerca cachata e ci restituisce una pagina sola.
  // Prima vivevano dentro SearchWithResults e giravano sui 200 risultati
  // ricevuti — che erano un campione, non il catalogo.
  const [filter, setFilter] = useState<RateHawkSearchFilter>({});
  const [sort, setSort] = useState<RateHawkSearchSort>({ field: "price", dir: "asc" });
  const [pageSize, setPageSize] = useState<RateHawkPageSize>(25);
  // Pagina RICHIESTA. Quella effettiva la conferma il server (`page` dal hook):
  // i filtri possono aver accorciato la lista sotto la pagina richiesta.
  const [requestedPage, setRequestedPage] = useState(1);
  // Incrementato dal pulsante "Cerca". È il solo modo di ripartire con i
  // parametri di ricerca (destinazione, date, ospiti): cambiarli non rifà la
  // ricerca da solo, cambiare un filtro sì.
  const [searchNonce, setSearchNonce] = useState(0);

  // ── Debounce, differenziato per gesto ─────────────────────────────────────
  //
  // Il filtro su cache costa ~100 ms: un'attesa lunga non risparmierebbe
  // chiamate, renderebbe solo la UI lenta. Testo e importi si digitano un
  // carattere alla volta e meritano più respiro delle caselle, dove due click
  // consecutivi distano già più del debounce.
  //
  // Il debounce gira sulla forma serializzata e non sull'oggetto: `filter` è
  // ricreato a ogni modifica, e un confronto per identità riarmerebbe il timer
  // a vuoto.
  const typedFilterKey = useMemo(
    () => JSON.stringify({
      name: filter.name,
      price_from: filter.price_from,
      price_to: filter.price_to,
    }),
    [filter.name, filter.price_from, filter.price_to],
  );
  const toggledFilterKey = useMemo(
    () => JSON.stringify({
      min_star_rating: filter.min_star_rating,
      include_unrated: filter.include_unrated,
      kind: filter.kind,
      meal_type: filter.meal_type,
      amenities: filter.amenities,
    }),
    [filter.min_star_rating, filter.include_unrated, filter.kind, filter.meal_type, filter.amenities],
  );

  const debouncedTyped = useDebouncedValue(typedFilterKey, 700);
  const debouncedToggled = useDebouncedValue(toggledFilterKey, 300);

  const effectiveFilter = useMemo<RateHawkSearchFilter>(
    () => ({ ...JSON.parse(debouncedToggled), ...JSON.parse(debouncedTyped) }),
    [debouncedTyped, debouncedToggled],
  );

  /**
   * Filtri e ordinamento cambiano l'insieme: restare a pagina 7 mostrerebbe una
   * pagina che nel nuovo insieme non significa più niente. Il reset sta nei
   * setter e non in un effetto per non far partire due ricerche — una con la
   * pagina vecchia e una con la nuova.
   */
  const handleFilterChange = useCallback((next: RateHawkSearchFilter) => {
    setFilter(next);
    setRequestedPage(1);
  }, []);

  const handleSortChange = useCallback((next: RateHawkSearchSort) => {
    setSort(next);
    setRequestedPage(1);
  }, []);

  const handlePageSizeChange = useCallback((next: RateHawkPageSize) => {
    setPageSize(next);
    setRequestedPage(1);
  }, []);

  // Seed a single room from the trip's known guest list (adults/children ages).
  const buildInitialRooms = useCallback((): RateHawkRoomGuestConfig[] => {
    const { adults, childrenAges: ages } = countGuestsFromRateHawkData(
      guestsData as { age?: number }[] | undefined,
      totalGuests,
    );
    return [{ adults, childrenAges: ages }];
  }, [guestsData, totalGuests]);

  // ── Sync on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setCityName("");
      setCoordinates(null);
      setRegionId(undefined);
      setSearchMode("region");
      setCheckIn(isTemplate ? referenceCheckIn : (currentDayDate?.split("T")[0] || ""));
      setCheckOut("");
      setRooms(buildInitialRooms());
      setRadius(10);
      setFilter({});
      setSort({ field: "price", dir: "asc" });
      setPageSize(25);
      setRequestedPage(1);
      // Azzerare il nonce riporta l'effetto di ricerca allo stato "mai
      // cercato": senza, riaprire la modale rilancerebbe subito una ricerca
      // con i parametri appena ripuliti.
      setSearchNonce(0);
      setStep("search");
      clearSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const rateHawkGuests = useMemo(() => buildRateHawkGuests(rooms), [rooms]);

  const showRoomsHint = useMemo(() => {
    if (rooms.length <= 1) return false;
    const first = rooms[0];
    return !rooms.every(
      (r) => r.adults === first.adults && r.childrenAges.length === first.childrenAges.length,
    );
  }, [rooms]);

  const canSearch = isMockMode
    ? !!(checkIn && checkOut)
    : !!(coordinates && checkIn && checkOut);

  // ── Selezione destinazione: le tre transizioni di modalità stanno qui, in
  // un solo posto, invece di essere sparse nei setter del componente figlio.

  const handleSelectRegion = useCallback((place: CityAutocompletePlace) => {
    setCityName(place.address);
    setCoordinates({ lat: place.lat, lng: place.lng });
    setRegionId(place.regionId);
    setSearchMode("region");
  }, []);

  const handleSelectCustomCoordinates = useCallback((coords: { lat: number; lng: number }) => {
    setCoordinates(coords);
    // In modalità coordinate la region non c'entra: azzerarla evita di mandare
    // a stays-search-rh un payload con entrambi i discriminanti.
    setRegionId(undefined);
    setCityName("");
    setSearchMode("geo");
  }, []);

  const handleClearDestination = useCallback(() => {
    setCityName("");
    setCoordinates(null);
    setRegionId(undefined);
    setSearchMode("region");
  }, []);

  /**
   * Avvia una ricerca. Non chiama la edge function: incrementa il nonce, e la
   * chiamata la fa l'effetto qui sotto. Così esiste un solo punto da cui parte
   * una ricerca, e premere "Cerca" con gli stessi filtri di prima continua a
   * funzionare (un effetto che dipendesse solo dai filtri non ripartirebbe).
   */
  const handleSearch = useCallback(() => {
    if (!checkIn || !checkOut) return;
    setRequestedPage(1);
    setSearchNonce((n) => n + 1);
  }, [checkIn, checkOut]);

  /**
   * L'unico punto che chiama la edge function.
   *
   * Riparte su: nonce (pulsante "Cerca"), filtri già debounced, ordinamento,
   * pagina, dimensione pagina. NON riparte quando cambiano destinazione, date
   * o ospiti: quelli sono i parametri della ricerca vera — cambiarli richiede
   * un click su "Cerca", perché ognuno costa una chiamata a ETG mentre un
   * cambio di filtro si serve dalla cache.
   *
   * Per lo stesso motivo i parametri di ricerca sono letti ma non osservati:
   * l'esclusione dalle dipendenze è voluta, non una svista.
   */
  useEffect(() => {
    if (searchNonce === 0) return;
    if (!checkIn || !checkOut) return;

    const params: RateHawkSearchParams = {
      checkin: checkIn,
      checkout: checkOut,
      guests: rateHawkGuests,
      latitude: coordinates?.lat ?? 41.9028, // fallback: Rome (mock only)
      longitude: coordinates?.lng ?? 12.4964,
      radius: radius * 1000, // km → metres
      search_mode: searchMode,
      region_id: searchMode === "region" ? regionId : undefined,
      currency: "EUR",
      residency: residency,
      filter: effectiveFilter,
      sort,
      page: requestedPage,
      page_size: pageSize,
    };

    void searchStays(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchNonce, effectiveFilter, sort, requestedPage, pageSize]);

  const handleSelectResult = useCallback(
    (result: RateHawkSearchResult) => {
      setStep("rates");
      fetchRates(result, {
        checkin: checkIn,
        checkout: checkOut,
        guests: rateHawkGuests,
        latitude: coordinates?.lat ?? 0,
        longitude: coordinates?.lng ?? 0,
        radius: radius * 1000,
        search_mode: searchMode,
        region_id: searchMode === "region" ? regionId : undefined,
        currency: "EUR",
        residency: residency,
      });
    },
    [fetchRates, checkIn, checkOut, rateHawkGuests, coordinates, radius, regionId, searchMode, residency],
  );

  const handleClose = useCallback(() => {
    setStep("search");
    setCityName("");
    setCoordinates(null);
    setRegionId(undefined);
    setSearchMode("region");
    setCheckIn(isTemplate ? referenceCheckIn : (currentDayDate?.split("T")[0] || ""));
    setCheckOut("");
    setRooms(buildInitialRooms());
    setRadius(10);
    setFilter({});
    setSort({ field: "price", dir: "asc" });
    setPageSize(25);
    setRequestedPage(1);
    setSearchNonce(0);
    setResidency("it");
    clearSearch();
    onClose();
  }, [clearSearch, onClose, currentDayDate, buildInitialRooms, isTemplate, referenceCheckIn]);

  const handleSelectRate = useCallback(
    (rate: RateHawkRate, room: RateHawkRoomGroup) => {
      if (!selectedHotel) return;

      const activity = convertRateHawkToPlace2BeAccommodation({
        rate,
        room,
        hotel: selectedHotel,
        checkInDate: checkIn,
        checkOutDate: checkOut,
        selectedRooms: rooms,
      });

      // Sui template le date della ricerca sono solo un riferimento per le tariffe:
      // l'alloggio si ancora ai giorni relativi, dal giorno selezionato per tante
      // notti quante ne copre la ricerca.
      const anchored = isTemplate && axis
        ? {
            ...activity,
            start_date: undefined,
            end_date: undefined,
            ...stayAnchor(axis, currentDayDate || '', checkIn && checkOut ? diffDays(checkIn, checkOut) : 1),
          }
        : activity;

      onSave(anchored);
      handleClose();
    },
    [selectedHotel, checkIn, checkOut, rooms, isTemplate, axis, currentDayDate, onSave, handleClose],
  );

  const handleBack = useCallback(() => {
    if (step === "rates") {
      clearRates();
      setStep("search");
    }
  }, [step, clearRates]);

  const title =
    step === "search"
      ? "Search Accommodations"
      : selectedHotel?.name || "Room Rates";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2">
          {step !== "search" && (
            <Button variant="ghost" size="sm" onClick={handleBack} className="p-1">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <span>{title}</span>
        </div>
      }
      size="full"
    >
      {/* Le tariffe di un template sono indicative: le date qui sono solo un
          riferimento per interrogare RateHawk, non finiscono nell'itinerario. */}
      {step === "search" && isTemplate && (
        <div className="mx-6 mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {t('builder.templateReferenceDatesNotice')}
        </div>
      )}

      {step === "search" && (
        <SearchWithResults
          cityName={cityName}
          setCityName={setCityName}
          coordinates={coordinates}
          setCoordinates={setCoordinates}
          searchMode={searchMode}
          onSelectRegion={handleSelectRegion}
          onSelectCustomCoordinates={handleSelectCustomCoordinates}
          onClearDestination={handleClearDestination}
          checkIn={checkIn}
          setCheckIn={setCheckIn}
          checkOut={checkOut}
          setCheckOut={setCheckOut}
          rooms={rooms}
          setRooms={setRooms}
          radius={radius}
          setRadius={setRadius}
          filter={filter}
          onFilterChange={handleFilterChange}
          sort={sort}
          onSortChange={handleSortChange}
          page={page}
          totalPages={totalPages}
          onPageChange={setRequestedPage}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          facets={facets}
          totalHotels={totalHotels}
          totalFiltered={totalFiltered}
          canSearch={canSearch}
          isSearching={isSearching}
          onSearch={handleSearch}
          results={searchResults}
          searchError={searchError}
          onSelectResult={handleSelectResult}
          startDate={startDate}
          endDate={endDate}
          residency={residency}
          onResidencyChange={setResidency}
        />
      )}

      {step === "rates" && (
        <RatesView
          roomGroups={roomGroups}
          hotel={selectedHotel}
          isLoading={isLoadingRates}
          error={ratesError}
          onSelect={handleSelectRate}
          showRoomsHint={showRoomsHint}
        />
      )}
    </Modal>
  );
}
